import {
  MANUAL_OPP_TYPES,
  TYPE_SELECTOR_OPTIONS,
} from "constants/opportunities";
import { PREFERRED_CONTRACT_TYPES } from "layout/settings/bestFitSettings/samGov/constants";

import {
  currencyField,
  dateField,
  externalLinkField,
  textField,
} from "../fieldBuilders";
import CustomerField from "../fields/CustomerField/CustomerField";

const COMMA_SEPARATED_PLACEHOLDER = "Comma-separated";

const OPPORTUNITY_STATUS_OPTIONS = [
  {
    value: "Active",
    label: "Active",
  },
  { value: "Inactive", label: "Inactive" },
];

const joinedListValue = (value) =>
  [value]
    .flat()
    .filter(Boolean)
    .map((item) => String(item).trim())
    .join(", ");

const customerField = (formData, onChangeValue, isUsGovCon) => ({
  key: "customer",
  label: isUsGovCon ? "Agency" : "Customer",
  type: "custom",
  render: () => (
    <CustomerField
      onChange={onChangeValue("customer")}
      value={formData?.customer}
      isUsGovCon={isUsGovCon}
    />
  ),
});

const commonFields = (
  formData,
  oppType,
  onChangeValue,
  focusField,
  defaultCurrencyCode
) => {
  const isOcds = oppType === MANUAL_OPP_TYPES.OCDS;
  const fields = [
    textField("title", "Name", formData, onChangeValue, {
      required: true,
      placeholder: "Name of the opportunity",
    }),
    textField("description", "Description", formData, onChangeValue, {
      placeholder: "Description of the opportunity",
    }),
    currencyField("estimatedValue", "Amount", formData, onChangeValue, {
      currencyCode: defaultCurrencyCode,
      autoFocus: focusField === "estimatedValue",
    }),
  ];

  if (!isOcds) {
    fields.push(
      dateField("dueDate", "Due Date", formData, onChangeValue, {
        autoFocus: focusField === "dueDate",
      })
    );
  }

  fields.push(externalLinkField(formData, onChangeValue));

  return fields;
};

const simpleFields = (formData, onChangeValue) => [
  customerField(formData, onChangeValue, false),
];

const usGovConFields = (formData, onChangeValue, setAsideOptions) => [
  customerField(formData, onChangeValue, true),
  textField("subTier", "Sub-Tier", formData, onChangeValue),
  textField("office", "Office", formData, onChangeValue),
  textField("externalId", "Solicitation Number", formData, onChangeValue),
  textField("naicsCode", "NAICS", formData, onChangeValue),
  textField("pscCode", "PSC", formData, onChangeValue),
  {
    key: "setAside",
    label: "Set Aside",
    type: "select",
    value: formData?.setAside,
    onChange: onChangeValue("setAside"),
    options: setAsideOptions,
    placeholder: "Select Set Aside",
  },
  {
    key: "type",
    label: "Notice Type",
    type: "select",
    value: formData?.type,
    onChange: onChangeValue("type"),
    options: PREFERRED_CONTRACT_TYPES,
  },
  dateField("createdDate", "Posted Date", formData, onChangeValue),
  textField(
    "popStreetAddress",
    "Place of Performance Street Address",
    formData,
    onChangeValue
  ),
  textField("popCity", "Place of Performance City", formData, onChangeValue),
  textField("popState", "Place of Performance State", formData, onChangeValue),
  textField(
    "popCountry",
    "Place of Performance Country",
    formData,
    onChangeValue
  ),
  textField("popZip", "Place of Performance Zip", formData, onChangeValue),
  textField("city", "Office Location City", formData, onChangeValue),
  textField("state", "Office Location State", formData, onChangeValue),
  textField(
    "countryCode",
    "Office Location Country Code",
    formData,
    onChangeValue
  ),
  textField("zipCode", "Office Location Zip Code", formData, onChangeValue),
  {
    key: "status",
    label: "Status",
    type: "select",
    value: formData?.status,
    onChange: onChangeValue("status"),
    options: OPPORTUNITY_STATUS_OPTIONS,
    placeholder: "What is the status of the contract",
  },
  textField(
    "primaryContactFullname",
    "Primary Contact Name",
    formData,
    onChangeValue
  ),
  textField(
    "primaryContactEmail",
    "Primary Contact Email",
    formData,
    onChangeValue
  ),
  textField(
    "primaryContactPhone",
    "Primary Contact Number",
    formData,
    onChangeValue
  ),
  textField(
    "secondaryContactFullname",
    "Secondary Contact Name",
    formData,
    onChangeValue
  ),
  textField(
    "secondaryContactEmail",
    "Secondary Contact Email",
    formData,
    onChangeValue
  ),
  textField(
    "secondaryContactPhone",
    "Secondary Contact Number",
    formData,
    onChangeValue
  ),
];

const ukTenderFields = (formData, onChangeValue, focusField) => [
  textField("buyerName", "Contracting Authority", formData, onChangeValue),
  textField("buyerType", "Buyer Type", formData, onChangeValue),
  textField("ocid", "OCID", formData, onChangeValue),
  textField("cpvCode", "CPV Code", formData, onChangeValue),
  textField("cpvDescription", "CPV Description", formData, onChangeValue),
  {
    key: "additionalCpvCodes",
    label: "Additional CPV Codes",
    type: "text",
    placeholder: COMMA_SEPARATED_PLACEHOLDER,
    value: joinedListValue(formData?.additionalCpvCodes),
    onChange: onChangeValue("additionalCpvCodes"),
  },
  textField("procurementStage", "Procurement Stage", formData, onChangeValue),
  {
    key: "frameworkAgreement",
    label: "Framework Agreement",
    type: "checkbox",
    value: formData?.frameworkAgreement,
    onChange: onChangeValue("frameworkAgreement"),
  },
  {
    ...textField("numberOfLots", "Number of Lots", formData, onChangeValue),
    type: "number",
  },
  {
    key: "suitability",
    label: "Suitability",
    type: "text",
    placeholder: COMMA_SEPARATED_PLACEHOLDER,
    value: joinedListValue(formData?.suitability),
    onChange: onChangeValue("suitability"),
  },
  textField("noticeType", "Notice Type", formData, onChangeValue),
  textField("procedureType", "Procedure Type", formData, onChangeValue),
  dateField("publishedDate", "Publication Date", formData, onChangeValue),
  dateField("submissionDeadline", "Closing Date", formData, onChangeValue, {
    autoFocus: focusField === "submissionDeadline",
  }),
  dateField("enquiryDeadline", "Enquiry Deadline", formData, onChangeValue),
  {
    key: "deliveryRegion",
    label: "Delivery Region",
    type: "text",
    placeholder: COMMA_SEPARATED_PLACEHOLDER,
    value: joinedListValue(formData?.deliveryRegion),
    onChange: onChangeValue("deliveryRegion"),
  },
  textField("status", "Status", formData, onChangeValue),
  textField(
    "buyerStreetAddress",
    "Contacting Authority Street Address",
    formData,
    onChangeValue
  ),
  textField(
    "buyerLocality",
    "Contacting Authority Locality",
    formData,
    onChangeValue
  ),
  textField(
    "buyerPostalCode",
    "Contacting Authority Postal Code",
    formData,
    onChangeValue
  ),
  textField(
    "buyerRegion",
    "Contacting Authority Region",
    formData,
    onChangeValue
  ),
  textField(
    "buyerCountry",
    "Contacting Authority Country",
    formData,
    onChangeValue
  ),
  textField(
    "buyerWebsite",
    "Contacting Authority Website",
    formData,
    onChangeValue
  ),
  textField("contactName", "Contact Name", formData, onChangeValue),
  textField("contactEmail", "Contact Email", formData, onChangeValue),
  textField("contactTelephone", "Contact Telephone", formData, onChangeValue),
];

const TYPE_SPECIFIC_FIELD_BUILDERS = {
  [MANUAL_OPP_TYPES.SIMPLE]: simpleFields,
  [MANUAL_OPP_TYPES.US_GOV_CON]: (formData, onChangeValue, focusField, setAsideOptions) =>
    usGovConFields(formData, onChangeValue, setAsideOptions),
  [MANUAL_OPP_TYPES.OCDS]: (formData, onChangeValue, focusField) =>
    ukTenderFields(formData, onChangeValue, focusField),
};

const getTypeLabel = (oppType) =>
  TYPE_SELECTOR_OPTIONS.find((option) => option.value === oppType)?.label;

const buildManualGroups = ({
  formData,
  oppType,
  onChangeValue,
  focusField,
  defaultCurrencyCode,
  setAsideOptions = [],
}) => {
  const typeSpecificBuilder = TYPE_SPECIFIC_FIELD_BUILDERS[oppType];
  const typeSpecificFields = typeSpecificBuilder
    ? typeSpecificBuilder(formData, onChangeValue, focusField, setAsideOptions)
    : [];
  const typeLabel = getTypeLabel(oppType);

  return [
    {
      title: "General",
      subtitle: "Basic details for this opportunity",
      fields: commonFields(
        formData,
        oppType,
        onChangeValue,
        focusField,
        defaultCurrencyCode
      ),
    },
    {
      title: typeLabel && `${typeLabel} Details`,
      subtitle: typeLabel && "Additional fields for this opportunity type",
      fields: typeSpecificFields,
    },
  ];
};

export default buildManualGroups;
